import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * The one place a surface asks "which scope am I in, and how do I change it?".
 *
 * <h3>Why this is a hook and not a component</h3>
 *
 * Two surfaces need the same behaviour and must not look the same. The platform
 * shell renders it inside the account menu in the v2 admin styling; the Smart
 * Agent Wizard renders a single compact control in the p7 palette, because the
 * wizard is deliberately chrome-less and a visitor there is not administering
 * anything. Sharing the markup would force one of them to wear the other's
 * clothes. Sharing the behaviour is what matters: the guard, the call, the
 * in-flight state, the error and the reload are identical, and before this they
 * existed only inside DashboardLayoutV2.
 *
 * <h3>Why the reload</h3>
 *
 * A membership switch re-issues the JWT against a different (customer, org,
 * tenant, role) tuple, so every cached list on the page is now answering for
 * the wrong scope. The reload is to a CLEAN pathname — any ?orgId=&tenantId=
 * in the current URL belongs to the scope being left, and carrying it into the
 * new one produces a refusal that reads like a failed switch when the switch
 * actually succeeded.
 */
export default function useScopeSwitch() {
  const { memberships, session, switchMembership } = useAuth();

  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  const activeMembershipId = session?.membershipId || null;
  const rows = useMemo(() => (Array.isArray(memberships) ? memberships : []), [memberships]);

  const active = useMemo(
    () => rows.find((m) => m.membershipId === activeMembershipId) || null,
    [rows, activeMembershipId],
  );

  /** The memberships a switch could move to — everything except the one in effect. */
  const others = useMemo(
    () => rows.filter((m) => m.membershipId !== activeMembershipId),
    [rows, activeMembershipId],
  );

  /**
   * What to show on screen.
   *
   * The names come from the session, which is re-derived from the live JWT on
   * every mount, so they describe the scope the platform is actually applying
   * rather than the one this tab last remembered. Falls back to the active
   * membership row when the server has not resolved a display name.
   */
  const scope = useMemo(() => ({
    role: session?.role || active?.role || null,
    customerName: session?.customerName || null,
    orgName: session?.orgName || active?.orgName || null,
    tenantName: session?.tenantName || active?.tenantName || null,
  }), [session, active]);

  const switchTo = useCallback(async (membershipId) => {
    if (!membershipId) return { ok: false };
    if (membershipId === activeMembershipId) return { ok: true, unchanged: true };

    setSwitching(true);
    setError(null);
    const result = await switchMembership(membershipId);
    setSwitching(false);

    if (!result?.ok) {
      setError(result?.error || 'We could not change your scope. Try again.');
      return result || { ok: false };
    }
    window.location.href = window.location.pathname;
    return result;
  }, [activeMembershipId, switchMembership]);

  return {
    memberships: rows,
    active,
    others,
    activeMembershipId,
    scope,
    switching,
    error,
    switchTo,
  };
}
